/**
 * Scope guides only. Public prices are withheld until approved figures and
 * currency are confirmed. Features are agreed in each written proposal.
 */

export interface Tier {
  name: string;
  /** One line on who the tier suits. */
  blurb: string;
  price: number | null;
  /** Explains how the quote is determined. */
  priceNote: string;
  features: string[];
  cta: string;
  /** The tier the eye should land on first. Exactly one per category. */
  featured?: boolean;
}

export interface PricingCategory {
  id: string;
  label: string;
  /** Shown under the toggle, so each category explains what it covers. */
  intro: string;
  tiers: Tier[];
}

export const pricing = {
  hero: {
    eyebrow: 'Pricing',
    headline: 'A clear scope. A clear price.',
    subcopy:
      'Compare possible project scopes, then request a written quote. We agree the deliverables, timeline, currency and price before work starts.',
  },

  categories: [
    {
      id: 'ai-app',
      label: 'AI App',
      intro:
        'AI-enabled applications scoped around one useful task, the data it needs and the interface your team will use. Features below are starting points for discussion.',
      tiers: [
        {
          name: 'Basic',
          blurb: 'Perfect for startups and small businesses.',
          price: null,
          priceNote: 'Quoted after discovery',
          features: [
            'Web or mobile interface, agreed in scope',
            'One defined AI task and its data sources',
            'User login and registration',
            'AI provider integration and output checks',
            'Support scope agreed in the proposal',
            'Delivery milestones agreed after discovery',
          ],
          cta: 'Request a written quote',
        },
        {
          name: 'Standard',
          blurb: 'Ideal for growing businesses.',
          price: null,
          priceNote: 'Quoted after discovery',
          featured: true,
          features: [
            'Web or mobile interface, agreed in scope',
            'AI workflow with human review points',
            'User login and registration',
            'Admin dashboard and activity history',
            'Evaluation against agreed example inputs',
            'Support scope agreed in the proposal',
            'Delivery milestones agreed after discovery',
          ],
          cta: 'Request a written quote',
        },
        {
          name: 'Premium',
          blurb: 'Designed for large and complex applications.',
          price: null,
          priceNote: 'Quoted after discovery',
          features: [
            'Web or mobile interface, agreed in scope',
            'Custom screen scope agreed in writing',
            'AI features and automation',
            'Custom UI/UX design',
            'Dedicated project manager',
            'Security requirements defined during discovery',
            'Priority support',
          ],
          cta: 'Discuss your AI app',
        },
      ],
    },
    {
      id: 'mvp-app',
      label: 'MVP App',
      intro:
        'The first shippable version of a product: enough to put in front of real users and learn from, without building everything first.',
      tiers: [
        {
          name: 'Basic',
          blurb: 'Validate one idea with real users.',
          price: null,
          priceNote: 'Quoted after discovery',
          features: [
            'Android or iOS app',
            'Up to 6 core screens',
            'User login and registration',
            'Analytics on the key journey',
            'Support scope agreed in the proposal',
            'Delivery milestones agreed after discovery',
          ],
          cta: 'Request a written quote',
        },
        {
          name: 'Standard',
          blurb: 'Launch-ready, with the admin side included.',
          price: null,
          priceNote: 'Quoted after discovery',
          featured: true,
          features: [
            'Android or iOS app',
            'Up to 15 screens',
            'Admin dashboard',
            'Payment gateway integration',
            'App store submission',
            'Support scope agreed in the proposal',
            'Delivery milestones agreed after discovery',
          ],
          cta: 'Request a written quote',
        },
        {
          name: 'Premium',
          blurb: 'For a funded build heading straight to market.',
          price: null,
          priceNote: 'Quoted after discovery',
          features: [
            'Android and iOS from one codebase',
            'Custom screen scope agreed in writing',
            'Custom UI/UX design',
            'Offline handling and push notifications',
            'Dedicated project manager',
            'Priority support',
          ],
          cta: 'Discuss your MVP',
        },
      ],
    },
    {
      id: 'commercial-app',
      label: 'Commercial App',
      intro:
        'Production software carrying real load and real money, built to be maintained by whoever inherits it.',
      tiers: [
        {
          name: 'Basic',
          blurb: 'A focused internal tool or line-of-business app.',
          price: null,
          priceNote: 'Quoted after discovery',
          features: [
            'Web or mobile application',
            'Role-based access control',
            'Third-party API integrations',
            'Automated test suite',
            'Support scope agreed in the proposal',
            'Delivery milestones agreed after discovery',
          ],
          cta: 'Request a written quote',
        },
        {
          name: 'Standard',
          blurb: 'A customer-facing product with payments.',
          price: null,
          priceNote: 'Quoted after discovery',
          featured: true,
          features: [
            'Web and mobile application',
            'Admin dashboard and reporting',
            'Payments and subscription billing',
            'CI pipeline with end-to-end tests',
            'Documentation and handover session',
            'Support scope agreed in the proposal',
            'Delivery milestones agreed after discovery',
          ],
          cta: 'Request a written quote',
        },
        {
          name: 'Premium',
          blurb: 'Complex systems, integrations and scale.',
          price: null,
          priceNote: 'Quoted after discovery',
          features: [
            'Multi-platform product suite',
            'Custom architecture and infrastructure',
            'AI features and workflow automation',
            'High-level security and audit logging',
            'Dedicated project manager',
            'Optional maintenance retainer',
          ],
          cta: 'Discuss your project',
        },
      ],
    },
  ] satisfies PricingCategory[],

  note: {
    heading: 'Not sure which fits?',
    body: 'These scopes help start the conversation. Tell us what you are building and we will identify the deliverables, dependencies and ongoing costs in a written proposal.',
    cta: { label: 'Talk to us', href: '/contact' },
  },
} as const;
